import React, { useState, useContext } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { HiEye, HiEyeOff } from "react-icons/hi";
import api, { setAuthToken } from "../services/api";
import { CartContext } from "../context/CartContext";
import { toast } from "react-toastify";
import logo from "../assets/logo.png";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { syncCartToServer } = useContext(CartContext);
  const redirectPath = searchParams.get("redirect");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      console.log("🔐 Logging in...");

      const res = await api.post("/login", formData);
      console.log("✅ Login successful:", res.data);

      const token = res.data.token;
      setAuthToken(token);
      localStorage.setItem("token", token);
      localStorage.setItem("userId", res.data.user._id);

      if (res.data.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }

      try {
        console.log("🔄 Syncing cart...");
        await syncCartToServer();
        console.log("✅ Cart synced successfully");
      } catch (syncErr) {
        console.warn("⚠️ Cart sync failed, but login successful:", syncErr);
      }

      // Check for pending voucher from Lucky Wheel
      const pendingVoucherId = localStorage.getItem("wonVoucherId");
      if (pendingVoucherId && pendingVoucherId !== "undefined") {
        console.log("🎟️ Found pending voucher:", pendingVoucherId);
        try {
          await api.post("/vouchers/claim", { voucherId: pendingVoucherId });
          localStorage.removeItem("wonVoucherId");
          toast.success(
            "🎉 Chúc mừng! Voucher trúng thưởng đã được lưu vào kho của bạn.",
          );
          console.log("✅ Pending voucher claimed successfully");
        } catch (claimErr) {
          console.error(
            "❌ Failed to claim pending voucher:",
            claimErr?.response?.data || claimErr.message,
          );
        }
      }

      if (
        res.data.user &&
        (res.data.user.isAdmin || res.data.user.role === "manager")
      ) {
        console.log(
          "✅ Admin/Manager user detected, redirecting to admin dashboard",
        );
        navigate("/admin");
        window.location.reload();
      } else if (redirectPath) {
        console.log(`✅ Redirecting to pending path: ${redirectPath}`);
        navigate(redirectPath);
        window.location.reload();
      } else {
        console.log("✅ Regular user, redirecting to home page");
        navigate("/");
        window.location.reload();
      }
    } catch (err) {
      console.error("❌ Login error:", err);

      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Đăng nhập thất bại. Vui lòng kiểm tra email và mật khẩu.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center relative overflow-hidden">
      {/* Fixed background that doesn't zoom and leave gaps */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 z-0"></div>
      
      {/* Decorative blobs - kept outside zoom to maintain sharpness and coverage */}
      <div className="absolute inset-0 opacity-20 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600 rounded-full filter blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-orange-600 rounded-full filter blur-[120px] animate-pulse" style={{ animationDelay: "1s" }}></div>
      </div>

      <div
        className="w-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10"
        style={{
          zoom: "0.8",
          MozTransform: "scale(0.8)",
          MozTransformOrigin: "center center",
        }}
      >

      <div className="max-w-md w-full relative z-10 transition-colors duration-500">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-white/10">
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 px-8 py-10 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 shadow-lg p-2">
              <img
                src={logo}
                alt="TaHaShoes Logo"
                className="w-full h-full rounded-full object-contain border-[1px] border-white"
              />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Chào mừng trở lại
            </h2>
            <p className="text-blue-100 text-sm">
              Đăng nhập để tiếp tục mua sắm
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-4 text-xs text-white/80 hover:text-white transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Quay về trang chủ
            </Link>
          </div>

          <div className="px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md animate-shake">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-red-500 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2 transition-colors"
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white text-gray-900 placeholder-gray-400"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700 mb-2 transition-colors"
                >
                  Mật khẩu
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 bg-white text-gray-900 placeholder-gray-400 "
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-600 focus:outline-none transition-colors"
                  >
                    {showPassword ? (
                      <HiEyeOff className="h-5 w-5" />
                    ) : (
                      <HiEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700 cursor-pointer transition-colors"
                  >
                    Ghi nhớ đăng nhập
                  </label>
                </div>

                <div className="text-sm">
                  <a
                    href="/forgot-password"
                    className="font-semibold text-blue-600 hover:text-blue-700 transition duration-150"
                  >
                    Quên mật khẩu?
                  </a>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-white font-semibold bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Đang đăng nhập...
                  </>
                ) : (
                  <>
                    Đăng nhập
                    <svg
                      className="ml-2 w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </>
                )}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 "></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium transition-colors">
                    Hoặc
                  </span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 transition-colors">
                  Chưa có tài khoản?{" "}
                  <a
                    href="/register"
                    className="font-semibold text-blue-600 hover:text-blue-700 transition duration-150"
                  >
                    Đăng ký ngay
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-300">
          Bằng việc đăng nhập, bạn đồng ý với{" "}
          <a href="#" className="text-blue-300 hover:text-blue-200 underline">
            Điều khoản dịch vụ
          </a>
        </p>
      </div>
    </div>
  </div>
);
};

export default Login;
